import { useState } from 'react';
import { EdgeProps, getStraightPath, useReactFlow, useStore } from 'reactflow';
import { RoadSegmentWithActions } from '../../models/yard-view.model';

const projectPointOntoSegment = (
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { x: number; y: number } => {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return { x: x1, y: y1 }
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))
  return { x: x1 + t * dx, y: y1 + t * dy }
}

// Renders a single full directional arrow (shaft + head) centered on the segment midpoint.
// axialOffset shifts the arrow center along the travel direction (px), e.g. to avoid icon overlap.
const renderCenterArrow = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  axialOffset = 0,
) => {
  const segDx = toX - fromX
  const segDy = toY - fromY
  const segLen = Math.sqrt(segDx * segDx + segDy * segDy)
  if (segLen === 0) return null
  const sux = segDx / segLen
  const suy = segDy / segLen

  const shaftLen = 12
  const headLen = 10
  const headHalfWidth = 4
  const totalLen = shaftLen + headLen

  if (segLen < totalLen + 10) return null

  const cx = (fromX + toX) / 2 + sux * axialOffset
  const cy = (fromY + toY) / 2 + suy * axialOffset
  const startX = cx - sux * (totalLen / 2)
  const startY = cy - suy * (totalLen / 2)
  const headBaseX = startX + sux * shaftLen
  const headBaseY = startY + suy * shaftLen
  const tipX = startX + sux * totalLen
  const tipY = startY + suy * totalLen
  const wx = -suy * headHalfWidth
  const wy = sux * headHalfWidth

  return (
    <>
      <line
        x1={startX}
        y1={startY}
        x2={headBaseX}
        y2={headBaseY}
        stroke='white'
        strokeWidth={1.5}
        style={{ pointerEvents: 'none' }}
      />
      <polygon
        points={`${tipX},${tipY} ${headBaseX + wx},${headBaseY + wy} ${headBaseX - wx},${headBaseY - wy}`}
        fill='white'
        style={{ pointerEvents: 'none' }}
      />
    </>
  )
}

const renderBlockedIcon = (cx: number, cy: number) => (
  <g style={{ pointerEvents: 'none' }}>
    <circle cx={cx} cy={cy} r={12} fill='white' />
    <g transform={`translate(${cx - 12}, ${cy - 12}) scale(1)`} fill='#FFC107'>
      <path d='M8.11098 8.111C7.07198 9.1495 6.49998 10.531 6.49998 12C6.49998 13.216 6.89698 14.368 7.62048 15.3195L15.3195 7.6205C14.367 6.897 13.216 6.5 12 6.5C10.5315 6.5 9.14998 7.0725 8.11098 8.111Z' />
      <path d='M12 17.5005C13.4685 17.5005 14.85 16.9285 15.8885 15.8895C16.9275 14.8505 17.5 13.4695 17.4995 12.001C17.4995 10.7855 17.1025 9.633 16.379 8.682L8.68098 16.38C9.63248 17.1035 10.784 17.5005 12 17.5005Z' />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12 5C13.869 5 15.627 5.728 16.949 7.0505C18.272 8.3725 19 10.1305 19 12C19 13.8695 18.272 15.6275 16.949 16.949C15.6275 18.272 13.869 19 12 19C10.1305 19 8.37298 18.272 7.05048 16.949C5.72798 15.6275 4.99998 13.8695 4.99998 12C4.99998 10.1305 5.72798 8.3725 7.05048 7.0505C8.37248 5.728 10.1305 5 12 5Z'
      />
    </g>
  </g>
)

export const RoadSegmentEdge = ({ source, target, data }: EdgeProps<RoadSegmentWithActions>) => {
  const [isHovered, setIsHovered] = useState(false)
  const sourceNode = useStore(s => s.nodeInternals.get(source))
  const targetNode = useStore(s => s.nodeInternals.get(target))
  const { screenToFlowPosition } = useReactFlow()

  if (!sourceNode?.positionAbsolute || !targetNode?.positionAbsolute) return null

  const sourceX = sourceNode.positionAbsolute.x + (sourceNode.width ?? 10) / 2
  const sourceY = sourceNode.positionAbsolute.y + (sourceNode.height ?? 10) / 2
  const targetX = targetNode.positionAbsolute.x + (targetNode.width ?? 10) / 2
  const targetY = targetNode.positionAbsolute.y + (targetNode.height ?? 10) / 2

  const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY })

  const isBlocked = data?.isBlocked ?? false
  const isTwoWay = data?.direction === 'TwoWay'
  const weightLimit = data?.weightLimit

  const midX = (sourceX + targetX) / 2
  const midY = (sourceY + targetY) / 2

  const segDx = targetX - sourceX
  const segDy = targetY - sourceY
  const segLen = Math.sqrt(segDx * segDx + segDy * segDy)
  const ux = segLen > 0 ? segDx / segLen : 0
  const uy = segLen > 0 ? segDy / segLen : 0

  // When blocked on a one-way segment, offset icon opposite to the arrow (22px each side = 44px apart).
  const iconSign = data?.direction === 'OneWayForward' ? -1 : 1
  const iconX = isBlocked && !isTwoWay ? midX + iconSign * 22 * ux : midX
  const iconY = isBlocked && !isTwoWay ? midY + iconSign * 22 * uy : midY

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    if (data?.id) {
      data.onContextMenu(data.id, { x: event.clientX, y: event.clientY })
    }
  }

  const getProjectedPosition = (event: React.MouseEvent) => {
    const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY })
    return projectPointOntoSegment(flowPos.x, flowPos.y, sourceX, sourceY, targetX, targetY)
  }

  const handleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!data?.id || !data?.onSplitSegment) return
    if (data.isDrawingWithPending) return
    data.onSplitSegment(data.id, getProjectedPosition(event))
  }

  const handleClick = (event: React.MouseEvent) => {
    if (!data?.isDrawingWithPending || !data?.id || !data?.onSplitSegment) return
    event.stopPropagation()
    data.onSplitSegment(data.id, getProjectedPosition(event))
  }

  return (
    <>
      {/* Road body */}
      <line
        x1={sourceX}
        y1={sourceY}
        x2={targetX}
        y2={targetY}
        stroke='#2d2d2d'
        strokeWidth={14}
        strokeLinecap='round'
        style={{ pointerEvents: 'none' }}
      />

      {/* Two-way dashes — rendered before blocked icon so icon covers them */}
      {isTwoWay && (
        <line
          x1={sourceX}
          y1={sourceY}
          x2={targetX}
          y2={targetY}
          stroke='white'
          strokeWidth={1.25}
          strokeDasharray='6,8'
          strokeLinecap='round'
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Blocked icon — after dashes (covers them on two-way), before arrow (arrow renders on top) */}
      {isBlocked && renderBlockedIcon(iconX, iconY)}

      {/* One-way arrow — always after blocked icon so it renders on top */}
      {!isTwoWay &&
        (data?.direction === 'OneWayForward'
          ? renderCenterArrow(sourceX, sourceY, targetX, targetY, isBlocked ? 22 : 0)
          : renderCenterArrow(targetX, targetY, sourceX, sourceY, isBlocked ? 22 : 0))}

      {/* Hover highlight */}
      {isHovered && (
        <path d={edgePath} fill='none' stroke='rgba(100,150,255,0.25)' strokeWidth={14} />
      )}

      {/* Invisible wider path for click/right-click hit area */}
      <path
        d={edgePath}
        data-road-segment={data?.id}
        fill='none'
        stroke='transparent'
        strokeWidth={14}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'context-menu' }}
      />

      {/* Weight limit label */}
      {weightLimit != null && (
        <text
          x={midX}
          y={midY - 8}
          textAnchor='middle'
          dominantBaseline='auto'
          style={{
            fontSize: 9,
            fill: '#888',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {weightLimit}t
        </text>
      )}
    </>
  )
}
